import { Divider } from "@blueprintjs/core";
import { ViewId, UserType, KnownOrganizationIds } from "@madhive/mad-sdk";
import { madSDK } from "lib/sdk";
import { Routes, INTERNAL_VIEWS } from "lib/constants/routes";
import { uniqBy } from "lodash";
import { RemoteConfigProperties, useFeatureFlag } from "hooks/useFeatureFlag";
import {
  selectCurrentUser,
  selectProductionViews
} from "appReducers/authReducer";
import { useSelector } from "react-redux";
import { css } from "@emotion/react";
import { Drawer } from "frontier/lib/components";
import { Button, Icons } from "@madhive/kit-ui";
import ListItem from "frontier/lib/components/List/ListItem";
import { FC, useMemo, Fragment, PropsWithChildren } from "react";
import { useLocation } from "react-router-dom";
import useManageDealsView from "hooks/useManageDealsView";
import useManagePricesView from "hooks/useManagePricesView";
import {
  mapViewIdToView,
  findActiveViewHref,
  SORT_INDEX_FOR_UNKNOWN_VIEW,
  OrderedViewCategories,
  ViewCategories
} from "./Router/utils";
import { AllowedOnly } from "./Router/WithAuth";
import InternalViewLink from "./InternalViewLink";
import ExternalToolLink from "./ExternalToolLink";
import ViewLink from "./ViewLink";
import { getPrismHost } from "frontier/lib/prism/prismHost";

/**
 * Edge fronts Prism at this path off the environment's API host. The
 * trailing slash is required: edge registers the route as
 * `/data-intelligence/` (see common/edgeheaders.DataIntelligencePathPrefix),
 * and its router treats `/x` and `/x/` as distinct registrations, so a bare
 * `/data-intelligence` falls through instead of reaching Prism.
 */
const PRISM_DATA_INTELLIGENCE_PATH = "/data-intelligence/";

export const style = {
  menuDrawer: css`
    .DrawerCloseContainer {
      min-height: 64px;
      background-color: var(--white);
    }
  `,
  activeStyle: css`
    color: var(--black);
    ::before {
      content: "";
      position: absolute;
      left: 0;
      top: 0;
      height: 100%;
      border-left: 2px solid var(--color-splash-primary);
      border-radius: var(--spacing-4);
    }
  `,
  listContainer: (active: boolean = false) => css`
    padding: var(--spacing-8) var(--spacing-24);
    color: var(--color-secondary);
    ${active ? style.activeStyle : undefined}
  `,
  fullList: css`
    width: auto;
  `,
  linkText: css`
    padding: 0;
    color: var(--color-secondary);
    text-decoration: none !important;
    font-weight: var(--font-weight-bold);
    font-size: var(--font-size-12);
  `,
  listItemIcon: css`
    min-width: 40px !important;
    color: var(--semi-dark);
  `,
  close: css`
    font-size: 2em;
  `,
  categoryHeading: css`
    font-size: var(--font-size-12);
  `,
  divider: css`
    margin: 0px;
  `,
  listItemContainer: css`
    padding: var(--spacing-24) var(--spacing-24) var(--spacing-8)
      var(--spacing-24);
  `,
  sectionHeading: css`
    border: none;
    cursor: default;
    color: var(--color-secondary);
  `,
  deepWaterHeading: css`
    font-size: var(--font-size-12);
    margin-top: 1em !important;
    padding: 0 var(--spacing-24);
  `
};

const ViewHeading: FC<PropsWithChildren> = props => {
  const { children } = props;
  // If there is no category, don't render the heading.
  if (!children) {
    return null;
  }
  return (
    <p
      css={[
        style.sectionHeading,
        style.categoryHeading,
        style.listItemContainer
      ]}
    >
      {children}
    </p>
  );
};

interface ViewSelectorDrawerProps {
  isOpen: boolean;
  views: ViewId[];
  width: number;
  onDrawerClose: () => void;
}

const ViewSelectorDrawer: FC<ViewSelectorDrawerProps> = ({
  views,
  isOpen,
  onDrawerClose,
  width
}) => {
  const user = useSelector(selectCurrentUser);
  const { primaryOrganizationId } = user!;

  const orgProductionViews = useSelector(selectProductionViews);

  const canViewManageDeals = useManageDealsView();
  const canViewManagePrices = useManagePricesView();

  const isFiveMinSeparationEnabled: boolean = useFeatureFlag(
    RemoteConfigProperties.INTERNAL_FIVE_MIN_SEPARATION
  );
  const isPublisherManagerEnabled: boolean = useFeatureFlag(
    RemoteConfigProperties.PUBLISHER_MANAGER
  );

  const isAudienceDataProvisioningEnabled: boolean = useFeatureFlag(
    RemoteConfigProperties.ENABLE_AUDIENCE_DATA_PROVISIONING
  );

  const isDmpDashboardEnabled: boolean = useFeatureFlag(
    RemoteConfigProperties.AUDIENCES_DMP_DASHBOARD
  );

  const hasSupplyTransparencyFeature: boolean = useFeatureFlag(
    RemoteConfigProperties.SUPPLY_SELECTOR_PRODUCT_VIEW
  );

  const isPublisherGroupsV2Enabled: boolean = useFeatureFlag(
    RemoteConfigProperties.PUBLISHER_GROUPS_V2
  );

  const isIASKeywordTargetingEnabled: boolean = useFeatureFlag(
    RemoteConfigProperties.IAS_KEYWORD_TARGETING
  );

  const isDirectUploadEnabled: boolean = useFeatureFlag(
    RemoteConfigProperties.DIRECT_1P_FILE_UPLOAD
  );

  const isMaverickExternalEnabled: boolean = useFeatureFlag(
    RemoteConfigProperties.MAVERICK_AI_SEGMENTS_EXTERNAL_UI
  );

  const isPromotedDashboardsNavEnabled: boolean = useFeatureFlag(
    RemoteConfigProperties.PROMOTED_DASHBOARDS_REPORTING_NAV
  );

  const isMaverickSellerEnabled: boolean = useFeatureFlag(
    RemoteConfigProperties.MAVERICK_SELLER
  );

  const isAiCreativeEnabled: boolean = useFeatureFlag(
    RemoteConfigProperties.MAVERICK_AI_CREATIVE
  );

  // Only Madhive admin users can access Audience Data Provisioning
  const canViewAudienceDataProvisioning =
    primaryOrganizationId === KnownOrganizationIds.MADHIVE_DEFAULT &&
    user?.isAdmin;

  const canViewMaverickAISegments =
    primaryOrganizationId === KnownOrganizationIds.MADHIVE_DEFAULT &&
    user?.isAdmin;

  // Called once here so useLocation doesn't get called twice and violate hook rules
  const currentPath = useLocation().pathname;

  /**
   * Note @MaximeHeckel:
   * Users might have views enabled that are no longer active / enabled at the org level.
   * If a view is turned off at the org level, it should be turned off as well at the user level.
   *
   * Hence, we need to filter out any user views that are not present in the org view list.
   *
   * Exception: Products view can be shown if user has permission and feature flag is enabled,
   * even if it's not in the org's base production views.
   * Exception: Publishers view can be shown if user has permission and feature flag is enabled,
   * even if it's not in the org's base production views.
   * Exception: IAS Keywords Uploader view can be shown if user has permission and feature flag is enabled,
   * even if it's not in the org's base production views.
   * Exception: First Party Data Onboarding can be shown if Direct Upload flag is enabled,
   * even if user doesn't have pii permission.
   */

  // Add FIRST_PARTY_DATA_ONBOARDING when Direct Upload flag is enabled
  const viewsWithDirectUpload = useMemo(() => {
    if (
      isDirectUploadEnabled &&
      !views.includes(ViewId.FIRST_PARTY_DATA_ONBOARDING)
    ) {
      return [...views, ViewId.FIRST_PARTY_DATA_ONBOARDING];
    }
    return views;
  }, [views, isDirectUploadEnabled]);

  // Add MAVERICK_AI_SEGMENTS_EXTERNAL when its flag is enabled
  const viewsWithMaverickExternal = useMemo(() => {
    if (
      isMaverickExternalEnabled &&
      !viewsWithDirectUpload.includes(ViewId.MAVERICK_AI_SEGMENTS_EXTERNAL)
    ) {
      return [...viewsWithDirectUpload, ViewId.MAVERICK_AI_SEGMENTS_EXTERNAL];
    }
    return viewsWithDirectUpload;
  }, [viewsWithDirectUpload, isMaverickExternalEnabled]);

  // Add MAVERICK_SELLER_CLIENTS whenever the user has MAVERICK_SELLER — it's
  // not independently assignable (see MAVERICK_SELLER_CLIENTS filter case
  // below), so anyone with Seller access automatically gets the Clients link.
  const viewsWithMaverickSellerClients = useMemo(() => {
    if (
      viewsWithMaverickExternal.includes(ViewId.MAVERICK_SELLER) &&
      !viewsWithMaverickExternal.includes(ViewId.MAVERICK_SELLER_CLIENTS)
    ) {
      return [...viewsWithMaverickExternal, ViewId.MAVERICK_SELLER_CLIENTS];
    }
    return viewsWithMaverickExternal;
  }, [viewsWithMaverickExternal]);

  // Add MAVERICK_AI_CREATIVE_LIBRARY whenever the user has MAVERICK_AI_CREATIVE
  // — not independently assignable, mirrors viewsWithMaverickSellerClients above.
  const viewsWithAiCreativeLibrary = useMemo(() => {
    if (
      viewsWithMaverickSellerClients.includes(ViewId.MAVERICK_AI_CREATIVE) &&
      !viewsWithMaverickSellerClients.includes(
        ViewId.MAVERICK_AI_CREATIVE_LIBRARY
      )
    ) {
      return [
        ...viewsWithMaverickSellerClients,
        ViewId.MAVERICK_AI_CREATIVE_LIBRARY
      ];
    }
    return viewsWithMaverickSellerClients;
  }, [viewsWithMaverickSellerClients]);

  // Add PROMOTED_DASHBOARDS when its flag is enabled — it's not assigned per
  // org or user, so the flag alone decides whether the link shows.
  const viewsWithPromotedDashboards = useMemo(() => {
    if (
      isPromotedDashboardsNavEnabled &&
      !viewsWithAiCreativeLibrary.includes(ViewId.PROMOTED_DASHBOARDS)
    ) {
      return [...viewsWithAiCreativeLibrary, ViewId.PROMOTED_DASHBOARDS];
    }
    return viewsWithAiCreativeLibrary;
  }, [viewsWithAiCreativeLibrary, isPromotedDashboardsNavEnabled]);

  const filteredViews = viewsWithPromotedDashboards.filter(view => {
    // Hide Publisher Groups when legacy publisher groups feature flag is disabled
    if (
      view === ViewId.MANAGE_PUBLISHER_GROUPS &&
      !isPublisherGroupsV2Enabled
    ) {
      return false;
    }
    // Allow Products view if user has it and feature flag is enabled
    if (view === ViewId.MANAGE_PRODUCTS && hasSupplyTransparencyFeature) {
      return true;
    }
    // Allow Publishers view if user has it and feature flag is enabled
    if (view === ViewId.PUBLISHER_MANAGER && isPublisherManagerEnabled) {
      return true;
    }

    if (view === ViewId.MANAGE_PRICES) {
      return canViewManagePrices;
    }

    // Allow IAS Keywords Uploader view if user has it and feature flag is enabled
    if (view === ViewId.IAS_KEYWORDS_UPLOADER && isIASKeywordTargetingEnabled) {
      return true;
    }

    // Allow First Party Data Onboarding if Direct Upload flag is enabled
    if (view === ViewId.FIRST_PARTY_DATA_ONBOARDING && isDirectUploadEnabled) {
      return true;
    }

    // Allow Maverick AI Segments (external) if feature flag is enabled
    if (
      view === ViewId.MAVERICK_AI_SEGMENTS_EXTERNAL &&
      isMaverickExternalEnabled
    ) {
      return true;
    }

    // Maverick Seller isn't tracked in orgProductionViews (it's assigned
    // per-user via Admin Users, not per-org) — the default filter below
    // would always exclude it. Presence in `views` already means the user
    // was assigned ViewId.MAVERICK_SELLER; also require the flag so the
    // item stays hidden while the section is mid-rollout.
    if (view === ViewId.MAVERICK_SELLER) {
      return isMaverickSellerEnabled;
    }

    // MAVERICK_SELLER_CLIENTS is never itself in a user's assigned viewIds —
    // it's injected above whenever MAVERICK_SELLER is present, so the same
    // flag gate is sufficient here.
    if (view === ViewId.MAVERICK_SELLER_CLIENTS) {
      return isMaverickSellerEnabled;
    }

    // Requires both org registration and the user's own grant (never
    // injected, unlike MAVERICK_SELLER above) — avoids a nav item that
    // AuthorizedOnly then rejects on click.
    if (view === ViewId.MAVERICK_AI_CREATIVE) {
      return (
        !!orgProductionViews[
          ViewId.MAVERICK_AI_CREATIVE as keyof typeof orgProductionViews
        ]?.production && isAiCreativeEnabled
      );
    }

    // MAVERICK_AI_CREATIVE_LIBRARY is never itself in a user's assigned
    // viewIds or org production views — it's injected above whenever the
    // user's raw grants include MAVERICK_AI_CREATIVE, before that view's own
    // org-production check runs. Re-apply that same check here so the
    // library link disappears along with AI Creative once the org's
    // registration is turned off, even for a user still holding a stale
    // personal grant.
    if (view === ViewId.MAVERICK_AI_CREATIVE_LIBRARY) {
      return (
        !!orgProductionViews[
          ViewId.MAVERICK_AI_CREATIVE as keyof typeof orgProductionViews
        ]?.production && isAiCreativeEnabled
      );
    }

    if (view === ViewId.PROMOTED_DASHBOARDS) {
      return isPromotedDashboardsNavEnabled;
    }

    // Default filter: view must be in org production views
    return orgProductionViews[view as keyof typeof orgProductionViews];
  });

  /** Add DEV-specific views to the filtered views list */
  const filteredViewsWithDevViews = useMemo(() => {
    const baseViews = [...filteredViews];

    // Add Products for DEV users (moved from Internal Tools to Admin)
    if (user?.isDev && !baseViews.includes(ViewId.MANAGE_PRODUCTS)) {
      baseViews.push(ViewId.MANAGE_PRODUCTS);
    }

    // Add Publishers for DEV users (moved from Internal Tools to Admin)
    if (
      user?.isDev &&
      isPublisherManagerEnabled &&
      !baseViews.includes(ViewId.PUBLISHER_MANAGER)
    ) {
      baseViews.push(ViewId.PUBLISHER_MANAGER);
    }

    if (
      user?.isDev &&
      canViewManagePrices &&
      !baseViews.includes(ViewId.MANAGE_PRICES)
    ) {
      baseViews.push(ViewId.MANAGE_PRICES);
    }

    return baseViews;
  }, [
    filteredViews,
    user?.isDev,
    isPublisherManagerEnabled,
    canViewManagePrices
  ]);

  /** Deduplicate any views that lead to the same URL. */
  const hydratedViews = useMemo(
    () =>
      uniqBy(
        filteredViewsWithDevViews.map(viewId => ({
          viewId,
          ...mapViewIdToView(viewId, orgProductionViews)
        })),
        view => view.href
      )
        .filter(view => view.sortIndex !== SORT_INDEX_FOR_UNKNOWN_VIEW)
        .filter(view => view.category !== ViewCategories.UNKNOWN),
    [filteredViewsWithDevViews, orgProductionViews]
  );

  // Derived from the route rather than tracked via clicks, so navigating in
  // from anywhere other than this sidebar (e.g. a Link on the page itself)
  // still highlights the right item.
  const activeItem = useMemo(
    () =>
      findActiveViewHref(
        currentPath,
        hydratedViews.map(view => view.href)
      ),
    [currentPath, hydratedViews]
  );

  /** Generate a mapping of ordered categories to its view links */
  const categoriesToViews = useMemo(
    () =>
      OrderedViewCategories.reduce((mapping, category) => {
        const orderedViewsInCategory = hydratedViews
          .filter(view => view.category === category)
          .sort((viewA, viewB) => viewA.sortIndex - viewB.sortIndex);

        if (orderedViewsInCategory.length) {
          return mapping.set(category, orderedViewsInCategory);
        }
        return mapping;
      }, new Map()),
    [hydratedViews]
  );

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onDrawerClose}
      position="left"
      size="315px"
      css={style.menuDrawer}
    >
      <div
        role="menubar"
        aria-label="main menu"
        css={css`
          width: ${width};
        `}
      >
        <ListItem
          className="DrawerCloseContainer"
          css={style.listItemContainer}
        >
          <Button
            variant="ghost"
            prefix={
              <Icons.Arrow direction="left" fill="var(--color-secondary)" />
            }
            tabIndex={0}
            onClick={onDrawerClose}
            ariaLabel="close menu"
          />
        </ListItem>
        <Divider css={style.divider} />
        {[...categoriesToViews].map(
          ([category, categoryViews]: [
            string,
            Array<{
              href: string;
              viewId: string;
              icon: JSX.Element;
              title: string;
            }>
          ]) => (
            <Fragment key={category}>
              <ViewHeading>{category}</ViewHeading>
              {categoryViews.map(view => (
                <ViewLink
                  key={view.viewId}
                  activeItem={activeItem}
                  onClick={onDrawerClose}
                  {...view}
                />
              ))}
            </Fragment>
          )
        )}
        <AllowedOnly userType={UserType.DEV}>
          <ViewHeading>Internal Tools</ViewHeading>
          {INTERNAL_VIEWS.filter(view => {
            if (view.route === Routes.MANAGE_DEALS) {
              return canViewManageDeals;
            }
            if (view.route === Routes.MANAGE_EXPERIMENTS) {
              return madSDK.isRootUser();
            }
            if (view.route === Routes.MANAGE_EXPERIMENT_META) {
              return madSDK.isRootUser();
            }
            if (view.route === Routes.FIVE_MIN_SEPARATION) {
              return isFiveMinSeparationEnabled;
            }
            if (view.route === Routes.AUDIENCE_DATA_PROVISIONING) {
              return (
                isAudienceDataProvisioningEnabled &&
                canViewAudienceDataProvisioning
              );
            }
            if (view.route === Routes.DMP_DASHBOARD) {
              return isDmpDashboardEnabled;
            }
            if (view.route === Routes.MAVERICK_AI_SEGMENTS) {
              return canViewMaverickAISegments;
            }
            if (view.route === Routes.MAVERICK_SELLER_PROPOSALS) {
              // Also require the view assignment here, not just the flag —
              // otherwise any DEV user sees this link regardless of whether
              // they actually have ViewId.MAVERICK_SELLER, and clicking it
              // immediately redirects via AuthorizedOnly (looks broken).
              return (
                isMaverickSellerEnabled &&
                views.includes(ViewId.MAVERICK_SELLER)
              );
            }
            if (view.route === Routes.MAVERICK_AI_CREATIVE) {
              // Same reasoning as MAVERICK_SELLER_PROPOSALS above — the page
              // still enforces AuthorizedOnly viewId={...}, so also require
              // the view assignment here to avoid a looks-broken redirect.
              return (
                isAiCreativeEnabled &&
                views.includes(ViewId.MAVERICK_AI_CREATIVE)
              );
            }
            if (view.route === Routes.MAVERICK_AI_CREATIVE_LIBRARY) {
              // Same permission as MAVERICK_AI_CREATIVE above — the library
              // page also gates on ViewId.MAVERICK_AI_CREATIVE, not a
              // separate grant (see MAVERICK_AI_CREATIVE_LIBRARY's own
              // definition).
              return (
                isAiCreativeEnabled &&
                views.includes(ViewId.MAVERICK_AI_CREATIVE)
              );
            }
            return true;
          }).map(view => (
            <InternalViewLink
              key={view.route}
              to={view.route}
              text={view.name}
              onClick={onDrawerClose}
            />
          ))}
          <ExternalToolLink
            id="internal-tools-data-intelligence-platform-link"
            href={`${getPrismHost()}${PRISM_DATA_INTELLIGENCE_PATH}`}
            text="Data Intelligence Platform"
            onClick={onDrawerClose}
          />
        </AllowedOnly>
      </div>
    </Drawer>
  );
};

export default ViewSelectorDrawer;
